import { LoadingOverlay, Modal, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { query } from '../../../util/api/query';
import type { SelectableBranchDto } from '../../../util/types/SelectableBranchDto';
import { useUpsertBranchForm } from '../hooks/useNewBranchForm';
import type { NewBranchDto } from '../types/BranchTypes';
import { UpsertBranchForm } from './UpsertBranchComponents';
import { Title } from '../../../components/Title';

interface NewBranchModalProps {
	close: () => void;
	opened: boolean;
	refreshTable: () => void;
}

export function NewBranchModal({
	close,
	opened,
	refreshTable,
}: NewBranchModalProps) {
	const { organisation } = useParams();
	const { form, inputs } = useUpsertBranchForm();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const [
		showStocksPrompt,
		{ open: openStocksPrompt, close: closeStocksPrompt },
	] = useDisclosure(false);

	const newBranchMutation = mutate<NewBranchDto>(
		`${organisation}/branches/new`,
		{ ...inputs, shouldCreateBranchStocks: false },
	);

	const newBranchWithStocksMutation = mutate<NewBranchDto>(
		`${organisation}/branches/new`,
		{ ...inputs, shouldCreateBranchStocks: true },
	);

	const pharmaciesQuery = query<SelectableBranchDto>(
		`${organisation}/branches/options`,
	);

	const submitBranch = async (shouldCreateBranchStocks: boolean) => {
		closeStocksPrompt();

		const mutation = shouldCreateBranchStocks
			? newBranchWithStocksMutation
			: newBranchMutation;

		await mutation.mutateAsync().then((response) => {
			if (response.isValid) {
				openSuccessModal();
			}
		});
	};

	const cancelClose = () => {
		refreshTable();
		close();
		form.reset();
		closeSuccessModal();
	};

	const successClose = () => {
		pharmaciesQuery.remove();
		pharmaciesQuery.refetch();

		cancelClose();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={cancelClose}
				title={<Title label={'New Branch'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<SmallModal
					isOpen={showSuccessModal}
					close={successClose}
					title={'Branch Added!'}
					body={
						'A new branch has been successfully added. Click OK to continue...'
					}
				/>
				<SmallModal
					isOpen={showStocksPrompt}
					close={() => submitBranch(false)}
					onComplete={() => submitBranch(true)}
                    okText={"Ok"}
                    cancelText={"No"}
					title={'Create Branch Stocks?'}
					body={
						'Do you also want to create branch stocks for all products at this ' +
						'branch? Click OK to create them, or Cancel to add the branch ' +
						'without them. Creating branch stocks may take a while.'
					}
				/>
				<LoadingOverlay
					visible={
						newBranchMutation.isLoading || newBranchWithStocksMutation.isLoading
					}
				/>
				<Stack w={'100%'} align={'center'}>
					<form onSubmit={form.onSubmit(() => openStocksPrompt())}>
						<UpsertBranchForm
							pharmaciesQuery={pharmaciesQuery}
							form={form}
							inputs={inputs}
							area={inputs.area}
						/>
						<FormButtons close={close} />
					</form>
				</Stack>
			</Modal>
		</>
	);
}
